import { Link, LinkProps } from 'react-router-dom';

import classNames from 'classnames';
import DestroyButton from 'components/DestroyButton';

interface RainbowCardProps {
  children?: React.ReactNode;
  checked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  onRemoveClick?: () => void;
  src?: string | string[];
  alt?: string;
  gray?: boolean;
  sideLabel?: string;
  shrink?: boolean;
  top?: React.ReactNode;
  unresponsive?: boolean;
  clickable?: boolean;
  forSidebar?: boolean;
}

type ClickOrToProps =
  | { onClick?: undefined; to?: undefined }
  | { onClick: () => void; to?: undefined }
  | { onClick?: undefined; to: LinkProps['to'] };

export const RainbowCard = ({
  children,
  to,
  src,
  alt = 'screenshot',
  checked,
  onCheckboxChange,
  onRemoveClick,
  onClick,
  sideLabel,
  gray,
  shrink,
  top,
  unresponsive,
  clickable,
  forSidebar
}: RainbowCardProps & ClickOrToProps) => {
  const hasMultiple = Array.isArray(src) && src.length > 1;
  const hasOne =
    (Array.isArray(src) && src.length === 1) || typeof src === 'string';

  const rainbowCardClasses = classNames('rainbow-card', {
    'rainbow-card--with-top': !!top,
    'rainbow-card--gray': gray,
    'rainbow-card--with-image': hasOne && !hasMultiple,
    'rainbow-card--with-multiple-image': hasMultiple,
    'rainbow-card--for-sidebar': forSidebar,
    'rainbow-card--draft': !!sideLabel,
    'rainbow-card--shrink': !!shrink,
    'rainbow-card--unresponsive': !!unresponsive,
    'rainbow-card--clickable': !!clickable,
    'rainbow-card--pointer': !!onClick
  });

  const renderScreenInPhone = () => {
    if (!src) {
      return null;
    }
    if (Array.isArray(src)) {
      if (!src.length) {
        return null;
      }

      if (src.length > 1) {
        return (
          <div className="rainbow-card__multiple-images">
            <div className="rainbow-card__image">
              <figure className="rainbow-card__phone">
                <img alt={alt} src={src[0]} />
              </figure>
            </div>
            <div className="rainbow-card__image rainbow-card__image--second">
              <figure className="rainbow-card__phone">
                <img alt={alt} src={src[1]} />
              </figure>
            </div>
            {src[2] && (
              <div className="rainbow-card__image rainbow-card__image--third">
                <figure className="rainbow-card__phone">
                  <img alt={alt} src={src[2]} />
                </figure>
              </div>
            )}
          </div>
        );
      }

      src = src[0];
    }

    return (
      <div className="rainbow-card__image">
        <figure className="rainbow-card__phone">
          <img alt={alt} src={src} />
        </figure>
      </div>
    );
  };

  const cardLink = to ? <Link className="rainbow-card__link" to={to} /> : null;

  const renderDraft = () => {
    if (!sideLabel) {
      return null;
    }
    return <div className="rainbow-card__draft">{sideLabel}</div>;
  };

  const renderCheckbox = () => {
    if (!onCheckboxChange) {
      return null;
    }
    return (
      <div className="card-checkbox">
        <label>
          <input
            className="card-checkbox__box"
            checked={checked}
            onChange={(e) => onCheckboxChange(e.target.checked)}
            type="checkbox"
          />
        </label>
      </div>
    );
  };

  const handleOnClick = () => {
    onClick ? onClick() : null;
  };

  return (
    <article className={rainbowCardClasses} onClick={handleOnClick}>
      {renderScreenInPhone()}
      {top}
      <div className="rainbow-card__body">
        {onRemoveClick && (
          <div className="rainbow-card__remove-button">
            <DestroyButton onClick={onRemoveClick} />
          </div>
        )}
        {children}
        {renderCheckbox()}
        {cardLink}
      </div>
      {renderDraft()}
    </article>
  );
};

export default RainbowCard;
